// src/composables/useDevices.ts
import { ref, computed, reactive } from "vue";
import {
  deviceApiService,
  type Device,
  DeviceApiError,
} from "@/services/DeviceApiService";
import { DeviceStorageService } from "@/services/DeviceStorageService";
import { NotificationService } from "@/util/notification";
import { ClipboardService } from "@/util/clipboard";
import { FormattersService } from "@/util/formatters";
import { ExportService } from "@/util/exportApi";
import { DeviceHelpers } from "@/util/deviceHelpers";
import type {
  DeviceForm,
  DeviceWithMetadata,
  RegisterDeviceRequest,
} from "@/types/device";

// Global state for devices
const devices = ref<DeviceWithMetadata[]>([]);
const loading = ref(false);
const initialized = ref(false);

// Device form state
const deviceForm = reactive<DeviceForm>({
  name: "",
  location: "",
  city: "",
  province: "",
  password: "",
  stream_key: "",
});

const isEditing = ref(false);
const editingDeviceId = ref<number | null>(null);
const submitting = ref(false);

export function useDevices() {
  // Computed properties
  const deviceCount = computed(() => devices.value.length);
  const onlineDevices = computed(
    () => devices.value.filter((device) => device.status === "online").length
  );
  const offlineDevices = computed(
    () => devices.value.filter((device) => device.status === "offline").length
  );

  /**
   * Initialize devices - this should be called when the component mounts
   */
  const initialize = async () => {
    if (initialized.value) return;

    // Migrate old storage format if exists
    const migrated = DeviceStorageService.migrateOldStorage();
    if (migrated > 0) {
      await NotificationService.info(
        `${migrated} perangkat berhasil dimigrasikan ke format baru`,
        "Migrasi Data"
      );
    }

    // Try to load devices from API first, fallback to local storage
    await loadDevicesFromAPI();
    initialized.value = true;

    console.log(`Initialized with ${devices.value.length} devices`);
  };

  /**
   * Load devices from API (if admin key available) or localStorage
   */
  const loadDevicesFromAPI = async () => {
    try {
      // Wait for Firebase to restore the session before deciding whether we
      // can reach the server — otherwise the first load races and falls back
      // to stale localStorage (which can resurrect deleted devices).
      const signedIn = await deviceApiService.ensureAuthReady();
      if (signedIn) {
        loading.value = true;

        // Load devices from API
        const response = await deviceApiService.listDevices({
          per_page: 100, // Load more devices at once
          sort_by: "created_at",
          sort_order: "desc",
        });

        devices.value = response.data;

        // Save to localStorage as backup
        DeviceStorageService.saveDevices(devices.value);

        console.log(`Loaded ${devices.value.length} devices from API`);
      } else {
        // Fallback to localStorage if no admin key
        devices.value = DeviceStorageService.loadDevices();
        console.log(
          `Loaded ${devices.value.length} devices from localStorage (no admin key)`
        );
      }
    } catch (error) {
      console.warn(
        "Failed to load devices from API, falling back to localStorage:",
        error
      );

      // Fallback to localStorage
      devices.value = DeviceStorageService.loadDevices();

      // Show warning to user
      if (error instanceof DeviceApiError && error.status === 401) {
        await NotificationService.warning(
          "Tidak dapat mengakses daftar perangkat dari server. Admin key tidak valid.",
          "Akses Terbatas"
        );
      }
    } finally {
      loading.value = false;
    }
  };

  /**
   * Refresh devices from API
   */
  const refreshDevicesFromAPI = async (
    silent = false
  ): Promise<boolean> => {
    try {
      loading.value = true;

      const signedIn = await deviceApiService.ensureAuthReady();
      if (!signedIn) {
        await NotificationService.warning(
          "Fitur refresh dari server tidak tersedia. Silakan masuk kembali.",
          "Akses Terbatas"
        );
        return false;
      }

      const response = await deviceApiService.listDevices({
        per_page: 100,
        sort_by: "created_at",
        sort_order: "desc",
      });

      devices.value = response.data;
      DeviceStorageService.saveDevices(devices.value);

      if (!silent) {
        await NotificationService.success(
          `Berhasil memuat ${devices.value.length} perangkat dari server`
        );
      }

      return true;
    } catch (error) {
      console.error("Error refreshing devices from API:", error);

      if (error instanceof DeviceApiError) {
        await NotificationService.error(
          error.message,
          "Gagal Refresh dari Server"
        );
      }

      return false;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Load devices from localStorage (deprecated - use DeviceStorageService)
   */
  const loadDevicesFromStorage = () => {
    devices.value = DeviceStorageService.loadDevices();
  };

  /**
   * Save devices to localStorage (deprecated - use DeviceStorageService)
   */
  const saveDevicesToStorage = () => {
    DeviceStorageService.saveDevices(devices.value);
  };

  /**
   * Add a device to the local list and save to storage
   */
  const addDeviceToList = (device: Device) => {
    const deviceWithMetadata: DeviceWithMetadata = {
      ...device,
      status: "unknown",
      lastSeen: undefined,
    };

    // Check if device already exists
    const existingIndex = devices.value.findIndex((d) => d.id === device.id);
    if (existingIndex >= 0) {
      devices.value[existingIndex] = deviceWithMetadata;
    } else {
      devices.value.push(deviceWithMetadata);
    }

    DeviceStorageService.saveDevices(devices.value);
  };

  /**
   * Remove a device from the local list
   */
  const removeDeviceFromList = (deviceId: number) => {
    const index = devices.value.findIndex((d) => d.id === deviceId);
    if (index >= 0) {
      devices.value.splice(index, 1);
      DeviceStorageService.saveDevices(devices.value);
    }
  };

  /**
   * Update device in the local list
   */
  const updateDeviceInList = (deviceId: number, updates: Partial<Device>) => {
    const index = devices.value.findIndex((d) => d.id === deviceId);
    if (index >= 0) {
      devices.value[index] = { ...devices.value[index], ...updates };
      DeviceStorageService.saveDevices(devices.value);
    }
  };

  /**
   * Register a new device
   */
  const addDevice = async (data: RegisterDeviceRequest): Promise<boolean> => {
    submitting.value = true;

    try {
      const response = await deviceApiService.registerDevice(data);

      // Create device object with the returned data
      const newDevice: Device = {
        id: response.id,
        name: data.name || null,
        location: data.location || null,
        city: data.city || null,
        province: data.province || null,
        stream_key: response.stream_key,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Add to local list
      addDeviceToList(newDevice);

      // Show success notification with device details
      await NotificationService.showRegistrationSuccess(
        response.id,
        response.stream_key
      );

      return true;
    } catch (error) {
      console.error("Error adding device:", error);

      if (error instanceof DeviceApiError) {
        await NotificationService.error(
          error.message,
          "Gagal Mendaftarkan Perangkat"
        );
      } else {
        await NotificationService.error(
          "Terjadi kesalahan saat mendaftarkan perangkat",
          "Gagal Mendaftarkan Perangkat"
        );
      }

      return false;
    } finally {
      submitting.value = false;
    }
  };

  /**
   * Update device information
   */
  const updateDevice = async (
    deviceId: number,
    password: string,
    updates: { name?: string; location?: string; city?: string; province?: string }
  ): Promise<boolean> => {
    submitting.value = true;

    try {
      // Check if we can use API endpoint
      if (deviceApiService.hasAdminKey()) {
        // Use API endpoint for update — admin key is sufficient, no device password needed
        const updatedDevice = await deviceApiService.updateDevice({
          id: deviceId,
          password: password || undefined,
          name: updates.name,
          location: updates.location,
          city: updates.city,
          province: updates.province,
        });

        // Update device in local list with response from API
        updateDeviceInList(deviceId, {
          name: updatedDevice.name,
          location: updatedDevice.location,
          city: updatedDevice.city,
          province: updatedDevice.province,
          updated_at: updatedDevice.updated_at,
        });

        await NotificationService.success(
          "Perangkat berhasil diperbarui melalui server"
        );
        return true;
      } else {
        // Fallback: Validate credentials first then update locally
        await deviceApiService.getDeviceData({ id: deviceId, password });

        // Update device in local list
        updateDeviceInList(deviceId, {
          name: updates.name || null,
          location: updates.location || null,
          updated_at: new Date().toISOString(),
        });

        await NotificationService.success(
          "Perangkat berhasil diperbarui (lokal)"
        );
        return true;
      }
    } catch (error) {
      console.error("Error updating device:", error);

      if (error instanceof DeviceApiError) {
        if (error.status === 400) {
          await NotificationService.error(
            "Password perangkat salah atau perangkat tidak ditemukan",
            "Gagal Memperbarui Perangkat"
          );
        } else if (error.status === 401) {
          await NotificationService.error(
            "Tidak memiliki akses untuk memperbarui perangkat melalui server",
            "Akses Ditolak"
          );
        } else {
          await NotificationService.error(
            error.message,
            "Gagal Memperbarui Perangkat"
          );
        }
      } else {
        await NotificationService.error(
          "Terjadi kesalahan saat memperbarui perangkat",
          "Gagal Memperbarui Perangkat"
        );
      }

      return false;
    } finally {
      submitting.value = false;
    }
  };

  /**
   * Delete a device
   */
  const deleteDevice = async (
    deviceId: number,
    password: string
  ): Promise<boolean> => {
    try {
      // Check if we can use API endpoint
      const signedIn = await deviceApiService.ensureAuthReady();
      if (signedIn) {
        // Use API endpoint for deletion
        await deviceApiService.deleteDevice({
          id: deviceId,
          password: password,
        });

        // Remove from local list
        removeDeviceFromList(deviceId);

        await NotificationService.success(
          "Perangkat berhasil dihapus dari server"
        );
        return true;
      } else {
        // Fallback: Validate credentials first then remove locally
        await deviceApiService.getDeviceData({ id: deviceId, password });

        // Remove from local list (can't actually delete from server without admin key)
        removeDeviceFromList(deviceId);

        await NotificationService.warning(
          "Perangkat dihapus dari daftar lokal. Untuk menghapus dari server, hubungi administrator.",
          "Hapus Lokal"
        );
        return true;
      }
    } catch (error) {
      console.error("Error deleting device:", error);

      if (error instanceof DeviceApiError) {
        if (error.status === 400) {
          await NotificationService.error(
            "Password perangkat salah atau perangkat tidak ditemukan",
            "Gagal Menghapus Perangkat"
          );
        } else if (error.status === 401) {
          await NotificationService.error(
            "Tidak memiliki akses untuk menghapus perangkat dari server",
            "Akses Ditolak"
          );
        } else if (error.status === 404) {
          await NotificationService.error(
            "Perangkat tidak ditemukan di server",
            "Gagal Menghapus Perangkat"
          );
        } else {
          await NotificationService.error(
            error.message,
            "Gagal Menghapus Perangkat"
          );
        }
      } else {
        await NotificationService.error(
          "Terjadi kesalahan saat menghapus perangkat",
          "Gagal Menghapus Perangkat"
        );
      }

      return false;
    }
  };

  /**
   * Regenerate stream key for a device
   */
  const regenerateStreamKey = async (
    deviceId: number,
    password: string
  ): Promise<string | null> => {
    try {
      const response = await deviceApiService.updateStreamKey({
        id: deviceId,
        password,
      });

      // Update device in local list
      updateDeviceInList(deviceId, {
        stream_key: response.stream_key,
        updated_at: new Date().toISOString(),
      });

      await NotificationService.showStreamKey(response.stream_key, true);
      return response.stream_key;
    } catch (error) {
      console.error("Error regenerating stream key:", error);

      if (error instanceof DeviceApiError) {
        await NotificationService.error(
          error.message,
          "Gagal Generate Stream Key"
        );
      } else {
        await NotificationService.error(
          "Terjadi kesalahan saat generate stream key",
          "Gagal Generate Stream Key"
        );
      }

      return null;
    }
  };

  /**
   * Test device connection
   */
  const testDeviceConnection = async (
    deviceId: number,
    password: string
  ): Promise<boolean> => {
    try {
      const deviceData = await deviceApiService.testConnection({
        id: deviceId,
        password,
      });

      // Update device status
      const deviceIndex = devices.value.findIndex((d) => d.id === deviceId);
      if (deviceIndex >= 0) {
        devices.value[deviceIndex].status = "online";
        devices.value[deviceIndex].lastSeen = new Date().toISOString();
        DeviceStorageService.saveDevices(devices.value);
      }

      await NotificationService.showConnectionResult(true, deviceData);
      return true;
    } catch (error) {
      console.error("Error testing device connection:", error);

      // Update device status to offline
      const deviceIndex = devices.value.findIndex((d) => d.id === deviceId);
      if (deviceIndex >= 0) {
        devices.value[deviceIndex].status = "offline";
        DeviceStorageService.saveDevices(devices.value);
      }

      let errorMessage = "Tidak dapat terhubung ke perangkat";
      if (error instanceof DeviceApiError) {
        errorMessage = error.message;
      }

      await NotificationService.showConnectionResult(
        false,
        undefined,
        errorMessage
      );
      return false;
    }
  };

  /**
   * Refresh device data from API
   */
  const refreshDevice = async (
    deviceId: number,
    password: string
  ): Promise<boolean> => {
    try {
      const deviceData = await deviceApiService.getDeviceData({
        id: deviceId,
        password,
      });

      // Update device in local list
      updateDeviceInList(deviceId, {
        name: deviceData.name,
        location: deviceData.location,
        stream_key: deviceData.stream_key,
      });

      return true;
    } catch (error) {
      console.error("Error refreshing device:", error);
      return false;
    }
  };

  /**
   * Bulk test all devices (requires passwords - not practical, but included for completeness)
   */
  const testAllDevicesConnection = async (
    passwords: Record<number, string>
  ) => {
    const results: { deviceId: number; success: boolean }[] = [];

    for (const device of devices.value) {
      const password = passwords[device.id];
      if (password) {
        const result = await testDeviceConnection(device.id, password);
        results.push({ deviceId: device.id, success: result });
      }
    }

    return results;
  };

  /**
   * Form management
   */
  const resetForm = () => {
    deviceForm.name = "";
    deviceForm.location = "";
    deviceForm.city = "";
    deviceForm.province = "";
    deviceForm.password = "";
    deviceForm.stream_key = "";
    isEditing.value = false;
    editingDeviceId.value = null;
  };

  const setFormData = (device: DeviceWithMetadata) => {
    deviceForm.name = device.name || "";
    deviceForm.location = device.location || "";
    deviceForm.city = device.city || "";
    deviceForm.province = device.province || "";
    deviceForm.password = "";
    deviceForm.stream_key = device.stream_key;
  };

  /**
   * Utility functions
   */
  const maskStreamKey = DeviceHelpers.maskStreamKey;

  const copyToClipboard = async (
    text: string,
    successMessage: string = "Berhasil disalin"
  ) => {
    const success = await ClipboardService.copy(text);
    if (success) {
      await NotificationService.toast(successMessage, "success");
    } else {
      await NotificationService.toast("Gagal menyalin ke clipboard", "error");
    }
  };

  const formatDate = FormattersService.formatDate;
  const formatRelativeTime = FormattersService.formatRelativeTime;

  const exportToCSV = () => {
    if (devices.value.length === 0) {
      NotificationService.warning("Tidak ada data untuk diekspor");
      return;
    }

    DeviceHelpers.exportDevicesToCSV(devices.value);
    NotificationService.success("Data berhasil diekspor ke CSV");
  };

  const exportToJSON = () => {
    if (devices.value.length === 0) {
      NotificationService.warning("Tidak ada data untuk diekspor");
      return;
    }

    DeviceHelpers.exportDevicesToJSON(devices.value);
    NotificationService.success("Data berhasil diekspor ke JSON");
  };

  const promptForPassword = NotificationService.promptPassword;

  return {
    // State
    devices,
    loading,
    deviceForm,
    isEditing,
    editingDeviceId,
    submitting,

    // Computed
    deviceCount,
    onlineDevices,
    offlineDevices,

    // Device management
    initialize,
    addDevice,
    updateDevice,
    deleteDevice,
    regenerateStreamKey,
    testDeviceConnection,
    refreshDevice,
    testAllDevicesConnection,
    loadDevicesFromAPI,
    refreshDevicesFromAPI,

    // Device list management
    addDeviceToList,
    removeDeviceFromList,
    updateDeviceInList,
    loadDevicesFromStorage,
    saveDevicesToStorage,

    // Form management
    resetForm,
    setFormData,

    // Utilities
    maskStreamKey,
    copyToClipboard,
    formatDate,
    formatRelativeTime,
    exportToCSV,
    exportToJSON,
    promptForPassword,
  };
}

export type { DeviceWithMetadata };
